<!-- views/TrainingDay.vue -->
<!-- Phase 2: Day別タスク一覧ページ -->

<template>
  <div class="training-day-page">
    <!-- ヘッダー -->
    <div class="page-header">
      <div class="header-content">
        <button @click="goBack" class="back-button">← 戻る</button>
        <div class="header-info">
          <h1>{{ dayInfo.name }}</h1>
          <p>{{ dayInfo.description }}</p>
        </div>
      </div>
    </div>

    <!-- ローディング -->
    <div v-if="isLoading" class="loading-container">
      <div class="spinner"></div>
      <p>タスクを読み込み中...</p>
    </div>

    <!-- エラー表示 -->
    <div v-else-if="error" class="error-container">
      <h3>❌ エラーが発生しました</h3>
      <p>{{ error }}</p>
      <button @click="loadTasks" class="retry-button">再試行</button>
    </div>

    <!-- タスク一覧 -->
    <div v-else class="tasks-container">
      <!-- Day統計 -->
      <div class="day-stats">
        <div class="stat-card">
          <div class="stat-icon">📝</div>
          <div class="stat-content">
            <span class="stat-value">{{ tasks.length }}</span>
            <span class="stat-label">総タスク数</span>
          </div>
        </div>
        
        <div class="stat-card">
          <div class="stat-icon">✅</div>
          <div class="stat-content">
            <span class="stat-value">{{ completedTasks }}</span>
            <span class="stat-label">完了済み</span>
          </div>
        </div>
        
        <div class="stat-card">
          <div class="stat-icon">⭐</div>
          <div class="stat-content">
            <span class="stat-value">{{ perfectTasks }}</span>
            <span class="stat-label">Perfect達成</span>
          </div>
        </div>
        
        <div class="stat-card">
          <div class="stat-icon">📊</div>
          <div class="stat-content">
            <span class="stat-value">{{ averageScore }}</span>
            <span class="stat-label">平均スコア</span>
          </div>
        </div>
      </div>

      <!-- 進捗バー -->
      <div class="progress-section">
        <div class="progress-header">
          <h3>進捗状況</h3>
          <span class="progress-percentage">{{ Math.round(progressPercentage) }}%</span>
        </div>
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: progressPercentage + '%' }"></div>
        </div>
      </div>

      <!-- タスクカード一覧 -->
      <div class="tasks-grid">
        <div 
          v-for="(task, index) in tasks" 
          :key="task.id"
          class="task-card"
          :class="getTaskCardClass(task)"
          @click="startTask(task, index)"
        >
          <div class="task-header">
            <div class="task-number">{{ index + 1 }}</div>
            <div class="task-status" :class="getTaskStatusClass(task)">
              {{ getTaskStatusText(task) }}
            </div>
          </div>
          
          <div class="task-content">
            <h4 class="task-sentence">{{ task.sentence }}</h4>
            <p class="task-translation">{{ task.translation }}</p>
            
            <div class="task-stats" v-if="getTaskStats(task)">
              <div class="task-stat">
                <span class="stat-label">試行回数</span>
                <span class="stat-value">{{ getTaskStats(task).attempts }}</span>
              </div>
              <div class="task-stat">
                <span class="stat-label">最高スコア</span>
                <span class="stat-value" :class="getScoreClass(getTaskStats(task).bestScore)">
                  {{ getTaskStats(task).bestScore }}
                </span>
              </div>
            </div>
          </div>
          
          <div class="task-actions">
            <button 
              class="start-button"
              :class="getStartButtonClass(task)"
            >
              {{ getStartButtonText(task) }}
            </button>
          </div>
        </div>
      </div>

      <!-- 完了メッセージ -->
      <div v-if="completedTasks === tasks.length && tasks.length > 0" class="completion-message">
        <div class="completion-icon">🎉</div>
        <h3>おめでとうございます！</h3>
        <p>{{ dayInfo.name }}を完了しました！</p>
        <div class="completion-stats">
          <span>Perfect達成: {{ perfectTasks }}文</span>
          <span>平均スコア: {{ averageScore }}点</span>
        </div>
        <button @click="goToNextDay" class="next-day-button" v-if="hasNextDay">
          次のDayへ →
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { supabase } from '../supabaseClient'

const router = useRouter()
const route = useRoute()

// 状態
const isLoading = ref(true)
const error = ref(null)
const tasks = ref([])
const taskStats = ref(new Map())

// 計算プロパティ
const dayId = computed(() => route.params.dayId)

const dayInfo = computed(() => {
  // Day情報を生成（実際の実装ではSupabaseから取得）
  const dayNumber = dayId.value.split('_').pop()
  return {
    name: `Day ${dayNumber}`,
    description: '発音練習を始めましょう！'
  }
})

const completedTasks = computed(() => {
  return tasks.value.filter(task => {
    const stats = getTaskStats(task)
    return stats && stats.attempts > 0
  }).length
})

const perfectTasks = computed(() => {
  return tasks.value.filter(task => {
    const stats = getTaskStats(task)
    return stats && stats.bestScore === 100
  }).length
})

const averageScore = computed(() => {
  const tasksWithScores = tasks.value.filter(task => {
    const stats = getTaskStats(task)
    return stats && stats.bestScore > 0
  })
  
  if (tasksWithScores.length === 0) return 0
  
  const totalScore = tasksWithScores.reduce((sum, task) => {
    return sum + getTaskStats(task).bestScore
  }, 0)
  
  return Math.round(totalScore / tasksWithScores.length)
})

const progressPercentage = computed(() => {
  if (tasks.value.length === 0) return 0
  return (completedTasks.value / tasks.value.length) * 100
})

const hasNextDay = computed(() => {
  // 次のDayが存在するかチェック（実際の実装ではSupabaseから確認）
  return false
})

// ライフサイクル
onMounted(async () => {
  await loadTasks()
})

// メソッド
const loadTasks = async () => {
  try {
    isLoading.value = true
    error.value = null
    
    // 練習タスクを取得
    const { data: tasksData, error: tasksError } = await supabase
      .from('training_tasks')
      .select('*')
      .order('difficulty', { ascending: true })
    
    if (tasksError) throw tasksError
    
    // Dayに応じてタスクをフィルタ（仮実装）
    tasks.value = (tasksData || []).slice(0, 5) // 最初の5文をDay 1として使用
    
    // 各タスクの統計を取得
    await loadTaskStats()
    
    console.log('✅ Dayタスク読み込み完了:', {
      dayId: dayId.value,
      tasks: tasks.value.length
    })
    
  } catch (err) {
    console.error('Dayタスク読み込み失敗:', err)
    error.value = err.message || 'タスクの読み込みに失敗しました'
  } finally {
    isLoading.value = false
  }
}

const loadTaskStats = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return
    
    // 各タスクの統計を取得
    const promises = tasks.value.map(async (task) => {
      const { data, error } = await supabase.rpc('get_total_stats', {
        p_user_id: user.id,
        p_task_id: task.id
      })
      
      if (!error && data && data.length > 0) {
        taskStats.value.set(task.id, data[0])
      }
    })
    
    await Promise.all(promises)
    
  } catch (error) {
    console.error('タスク統計読み込み失敗:', error)
  }
}

const getTaskStats = (task) => {
  return taskStats.value.get(task.id) || { attempts_total: 0, best_overall: 0 }
}

const getTaskCardClass = (task) => {
  const stats = getTaskStats(task)
  if (stats.bestScore === 100) return 'perfect'
  if (stats.attempts > 0) return 'completed'
  return 'not-started'
}

const getTaskStatusClass = (task) => {
  const stats = getTaskStats(task)
  if (stats.bestScore === 100) return 'perfect'
  if (stats.attempts > 0) return 'completed'
  return 'not-started'
}

const getTaskStatusText = (task) => {
  const stats = getTaskStats(task)
  if (stats.bestScore === 100) return 'Perfect!'
  if (stats.attempts > 0) return '完了'
  return '未開始'
}

const getStartButtonClass = (task) => {
  const stats = getTaskStats(task)
  if (stats.bestScore === 100) return 'perfect'
  if (stats.attempts > 0) return 'retry'
  return 'start'
}

const getStartButtonText = (task) => {
  const stats = getTaskStats(task)
  if (stats.bestScore === 100) return '再練習'
  if (stats.attempts > 0) return '再挑戦'
  return '開始'
}

const getScoreClass = (score) => {
  if (score >= 90) return 'excellent'
  if (score >= 70) return 'good'
  return 'needs-improvement'
}

const startTask = (task, index) => {
  router.push({
    name: 'TrainingTask',
    params: { taskId: task.id },
    query: { dayId: dayId.value, index: index }
  })
}

const goBack = () => {
  router.push('/training')
}

const goToNextDay = () => {
  // 次のDayに移動（実際の実装では適切なDay IDを計算）
  console.log('次のDayへ移動')
}
</script>

<style scoped>
.training-day-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2rem;
}

.page-header {
  margin-bottom: 3rem;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.back-button {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: bold;
}

.back-button:hover {
  background: rgba(255, 255, 255, 0.3);
}

.header-info h1 {
  color: white;
  font-size: 2.5rem;
  margin: 0 0 0.5rem 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.header-info p {
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.1rem;
  margin: 0;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: white;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-container {
  text-align: center;
  color: white;
  background: rgba(239, 68, 68, 0.1);
  padding: 2rem;
  border-radius: 12px;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.retry-button {
  background: #ef4444;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  cursor: pointer;
  margin-top: 1rem;
}

.tasks-container {
  max-width: 1200px;
  margin: 0 auto;
}

.day-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  font-size: 2rem;
}

.stat-content {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: bold;
  color: #1f2937;
}

.stat-label {
  font-size: 0.9rem;
  color: #6b7280;
}

.progress-section {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.progress-header h3 {
  color: #1f2937;
  margin: 0;
}

.progress-percentage {
  font-size: 1.2rem;
  font-weight: bold;
  color: #3b82f6;
}

.progress-bar {
  width: 100%;
  height: 12px;
  background: #e5e7eb;
  border-radius: 6px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #1d4ed8);
  transition: width 0.3s ease;
}

.tasks-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1.5rem;
}

.task-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border: 2px solid transparent;
}

.task-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.task-card.perfect {
  border-color: #10b981;
  background: linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%);
}

.task-card.completed {
  border-color: #3b82f6;
  background: linear-gradient(135deg, #ffffff 0%, #eff6ff 100%);
}

.task-card.not-started {
  border-color: #e5e7eb;
}

.task-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.task-number {
  background: #f3f4f6;
  color: #6b7280;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.task-status {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: bold;
}

.task-status.perfect {
  background: #d1fae5;
  color: #065f46;
}

.task-status.completed {
  background: #dbeafe;
  color: #1e40af;
}

.task-status.not-started {
  background: #f3f4f6;
  color: #6b7280;
}

.task-content {
  margin-bottom: 1rem;
}

.task-sentence {
  font-size: 1.1rem;
  font-weight: bold;
  color: #1f2937;
  margin-bottom: 0.5rem;
}

.task-translation {
  color: #6b7280;
  margin-bottom: 1rem;
}

.task-stats {
  display: flex;
  gap: 1rem;
}

.task-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.task-stat .stat-label {
  font-size: 0.8rem;
  color: #6b7280;
  margin-bottom: 0.25rem;
}

.task-stat .stat-value {
  font-size: 1rem;
  font-weight: bold;
  color: #1f2937;
}

.task-stat .stat-value.excellent {
  color: #10b981;
}

.task-stat .stat-value.good {
  color: #f59e0b;
}

.task-stat .stat-value.needs-improvement {
  color: #ef4444;
}

.task-actions {
  text-align: center;
}

.start-button {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
}

.start-button:hover {
  background: #2563eb;
  transform: translateY(-1px);
}

.start-button.perfect {
  background: #10b981;
}

.start-button.perfect:hover {
  background: #059669;
}

.start-button.retry {
  background: #f59e0b;
}

.start-button.retry:hover {
  background: #d97706;
}

.completion-message {
  text-align: center;
  background: white;
  border-radius: 12px;
  padding: 3rem;
  margin-top: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.completion-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.completion-message h3 {
  color: #1f2937;
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.completion-message p {
  color: #6b7280;
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
}

.completion-stats {
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-bottom: 2rem;
  color: #6b7280;
}

.next-day-button {
  background: #10b981;
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
}

.next-day-button:hover {
  background: #059669;
  transform: translateY(-1px);
}

@media (max-width: 768px) {
  .training-day-page {
    padding: 1rem;
  }
  
  .header-content {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .header-info h1 {
    font-size: 2rem;
  }
  
  .day-stats {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .tasks-grid {
    grid-template-columns: 1fr;
  }
  
  .completion-stats {
    flex-direction: column;
    gap: 1rem;
  }
}
</style>
